/* =========================================================================
   ClueVine — challenge-link handler
   Opens the exact puzzle a friend shared (#play=cipher.d270 or
   #play=cipher.hard.K3J9X2A, optionally with a score to beat). Mounted inside
   the main shell, so a brand-new player finishes onboarding first and then
   lands straight in the shared puzzle. Also reacts to links opened while the
   app is already running.
   ========================================================================= */

import { useEffect } from 'react';
import { parseChallengeHash, type ChallengeLink } from '../engine/links';
import { getMode } from '../engine/registry';
import { dayNumber } from '../engine/seed';
import { useGame } from '../state/GameContext';
import { useNav } from '../state/NavContext';
import { useSiteConfig } from '../state/SiteConfigContext';
import { useToast } from './Toast';

// Captured at module load: the link that launched the app survives onboarding.
let pending: ChallengeLink | null =
  typeof window !== 'undefined' ? parseChallengeHash(window.location.hash) : null;

function clearHash(): void {
  try {
    window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
  } catch {
    /* ignore */
  }
}

export function LinkHandler() {
  const game = useGame();
  const { navigate } = useNav();
  const { isModeEnabled } = useSiteConfig();
  const toast = useToast();

  useEffect(() => {
    const open = (link: ChallengeLink) => {
      clearHash();
      const mode = getMode(link.ref.modeId);
      if (!mode || !isModeEnabled(mode.id)) {
        toast.show('That puzzle’s mode isn’t available here', 'error');
        return;
      }
      const challenge = link.beat || link.by ? { beat: link.beat, by: link.by } : null;
      if (link.ref.kind === 'daily') {
        const today = dayNumber();
        if (link.ref.day > today) {
          toast.show(`Daily #${link.ref.day} isn’t out yet — check back soon`);
          return;
        }
        const outcome = game.start({ modeId: mode.id, playMode: 'daily', day: link.ref.day, challenge });
        if (outcome === 'done') {
          toast.show(`You’ve already played ${mode.name} #${link.ref.day}`);
          navigate('home');
        } else if (outcome !== 'unavailable') {
          navigate('play');
        }
        return;
      }
      if (!mode.difficulties.some((d) => d.id === (link.ref.kind === 'endless' ? link.ref.difficultyId : ''))) {
        toast.show('That puzzle link looks broken', 'error');
        return;
      }
      game.start({
        modeId: mode.id,
        playMode: 'endless',
        difficultyId: link.ref.difficultyId,
        salt: link.ref.salt,
        challenge,
      });
      navigate('play');
    };

    if (pending) {
      const link = pending;
      pending = null;
      open(link);
    }

    const onHash = () => {
      const link = parseChallengeHash(window.location.hash);
      if (link) open(link);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
    // Mount-once: the handlers read fresh state through the stable context APIs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
